import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useBookings, useVehicles } from "../hooks/useRentalData";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { addMonths, endOfMonth, sameDay, dateOnly, buildGrid, categorize, computeTodayAvailability } from "../lib/calendarUtils";
import { isChauffeurOnly } from "../lib/permissions";
import TodayAvailabilityPanel from "../components/TodayAvailabilityPanel";
import DayBookingsDialog from "../components/DayBookingsDialog";

const CATS = ["Coupes", "Sedans", "SUVs", "END"];

// SALES-022: internal holds render slate-grey with diagonal hatching — reads as
// "blocked" at a glance and can't be mistaken for a revenue type (purple is
// chauffeur/tour, pink insurance). One string for the month bars, one for the
// solid-palette availability cells.
const INTERNAL_BAR = "border-slate-400 bg-slate-500/25 bg-[repeating-linear-gradient(45deg,rgba(148,163,184,0.4)_0px,rgba(148,163,184,0.4)_5px,transparent_5px,transparent_10px)]";
// CAL-001: a day held only by a booking waiting for approval is a faded yellow,
// so it never reads as a confirmed booking. Solid yellow was taken for "booked".
const PENDING_CELL = "bg-yellow-400/30";
const INTERNAL_CELL = "bg-slate-500/50 bg-[repeating-linear-gradient(45deg,rgba(15,23,42,0.45)_0px,rgba(15,23,42,0.45)_4px,transparent_4px,transparent_8px)]";

// SALES-012: the view lives in the URL, in ISO, with readable keys — these URLs
// get pasted between people. `toDateString()` stays an internal detail.
const pad2 = (n) => String(n).padStart(2, "0");
const monthParamOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const dayParamOf = (d) => `${monthParamOf(d)}-${pad2(d.getDate())}`;
// Both parsers answer null for anything they do not fully recognise — a junk
// param falls back to the default view instead of rendering a broken grid.
const parseMonthParam = (s) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(s || ""));
  if (!m) return null;
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return null;
  return new Date(Number(m[1]), mo - 1, 1);
};
const parseDayParam = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  if (!m) return null;
  const mo = Number(m[2]), da = Number(m[3]);
  const d = new Date(Number(m[1]), mo - 1, da);
  // Rejects 2026-02-31 and friends, which Date would roll into March.
  if (d.getMonth() !== mo - 1 || d.getDate() !== da) return null;
  return d;
};

const FILTERS = [
  { value: "availability", label: "Fleet Availability", match: null },
  { value: "today", label: "Available Today", match: null },
  { value: "pre_booking", label: "Pre-bookings", match: (b) => b.status === "pre_booking" },
  { value: "rental", label: "Rentals (Current)", match: (b) => b.booking_type === "rental" && b.status === "current" },
  { value: "insurance", label: "Insurance replacement", match: (b) => b.booking_type === "rental" && b.booking_category === "insurance" && b.status !== "cancelled" },
  { value: "chauffeur", label: "Chauffeur / Tours", match: (b) => (b.booking_type === "chauffeur" || b.booking_type === "tour") && b.status !== "pre_booking" && b.status !== "cancelled" },
  { value: "internal", label: "Internal", match: (b) => b.booking_type === "internal" && b.status !== "cancelled" },
];

// Build event spans grouped by week-row. Returns array of {row, colStart, colSpan, event, color}
function buildSpans(events, grid, colorFor) {
  const spans = [];
  const lanes = []; // per-row lane usage
  for (let r = 0; r < grid.length / 7; r++) lanes.push([]);
  for (const e of events) {
    const s = dateOnly(e.start_datetime); const ee = dateOnly(e.end_datetime) || s;
    if (!s) continue;
    for (let r = 0; r < grid.length / 7; r++) {
      const wkStart = grid[r * 7], wkEnd = grid[r * 7 + 6];
      if (ee < wkStart || s > wkEnd) continue;
      const segStart = s < wkStart ? wkStart : s;
      const segEnd = ee > wkEnd ? wkEnd : ee;
      const colStart = Math.round((segStart - wkStart) / 86400000) + 1;
      const colSpan = Math.round((segEnd - segStart) / 86400000) + 1;
      // find a free lane
      let lane = 0;
      while (lanes[r].some((u) => u.lane === lane && !(u.end < colStart || u.start > colStart + colSpan - 1))) lane++;
      lanes[r].push({ lane, start: colStart, end: colStart + colSpan - 1 });
      spans.push({ row: r, colStart, colSpan, lane, event: e, color: colorFor(e) });
    }
  }
  return spans;
}

function colorForEvent(e) {
  if (e.booking_type === "internal") {
    return { bar: INTERNAL_BAR, text: "text-slate-100" };
  }
  if (e.booking_type === "chauffeur" || e.booking_type === "tour") {
    return { bar: "bg-purple-500/30 border-purple-500/40", text: "text-purple-100" };
  }
  if (e.booking_type === "rental" && e.booking_category === "insurance") {
    // Pink, not the destructive red it used to share with nothing in this view
    // and with retail rentals in the availability grid. Same /30 wash and
    // full-strength border as the purple above, and far enough round the wheel
    // from both (pink 330°, red 0°, purple 271°) to read at a glance.
    return { bar: "bg-pink-500/30 border-pink-500", text: "text-pink-100" };
  }
  return { bar: "bg-primary/30 border-primary", text: "text-foreground" };
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isInvestor = user?.role === "investor";
  const { data: bookings = [] } = useBookings({ refetchInterval: 20000, refetchOnWindowFocus: true });
  const { data: vehicles = [] } = useVehicles();
  const location = useLocation();
  // SALES-012: tab, month, category and both highlights are DERIVED from the
  // URL, not mirrored in state — one source of truth, so a reload or a pasted
  // link reproduces the view and Back restores it.
  const [params, setParams] = useSearchParams();
  // SALES-023: reps live on Fleet Availability, so it is first and the default.
  // Investors keep landing on `rental` (they can't see availability/today), and
  // chauffeur-only logins keep their current default — this page is their home
  // and their default must not move.
  const defaultTab = isInvestor ? "rental" : isChauffeurOnly(user) ? "pre_booking" : "availability";
  const tabParam = params.get("tab");
  const tabValid = FILTERS.some((f) => f.value === tabParam)
    && !(isInvestor && (tabParam === "today" || tabParam === "availability"));
  const tab = tabValid ? tabParam : defaultTab;
  const monthParam = params.get("month");
  const cursor = useMemo(() => parseMonthParam(monthParam) || new Date(), [monthParam]);
  const hlVehicle = params.get("vehicle") || null;
  const dayParam = params.get("day");
  const hlDay = useMemo(() => parseDayParam(dayParam), [dayParam]);
  // The grid compares on toDateString(); only the URL is ISO.
  const hlDate = hlDay ? hlDay.toDateString() : null;
  const [picker, setPicker] = useState(null);
  const fmtDT = (s) => s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const grid = useMemo(() => buildGrid(cursor), [cursor]);
  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });
  const filter = FILTERS.find((f) => f.value === tab);
  const events = useMemo(
    () => (filter && filter.match ? bookings.filter(filter.match) : []),
    [filter, bookings]
  );
  const spans = useMemo(() => buildSpans(events, grid, colorForEvent), [events, grid]);

  // Availability matrix: vehicles × days in current month
  const monthDays = useMemo(() => {
    const last = endOfMonth(cursor).getDate();
    const arr = []; for (let i = 1; i <= last; i++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    return arr;
  }, [cursor]);
  const grouped = useMemo(() => {
    const g = { Coupes: [], Sedans: [], SUVs: [], END: [] };
    [...vehicles].filter((v) => v.status !== "retired").sort((a,b) => (a.make||"").localeCompare(b.make||"") || (a.model||"").localeCompare(b.model||""))
      .forEach(v => g[categorize(v)].push(v));
    return g;
  }, [vehicles]);
  const cats = useMemo(() => CATS.filter((c) => grouped[c].length > 0), [grouped]);
  const catParam = params.get("cat");
  // A category named in the URL that holds no vehicles falls back to the first
  // non-empty one; an empty tab would render a grid with no rows.
  const cat = cats.includes(catParam) ? catParam : (cats[0] || CATS[0]);

  // The one writer. A key at its default is DELETED rather than written, so a
  // pristine Calendar keeps a clean URL, and `replace` keeps Back as "leave the
  // page" instead of a walk back through every highlight click.
  const setView = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    }
    if (next.get("tab") === defaultTab) next.delete("tab");
    if (next.get("month") === monthParamOf(new Date())) next.delete("month");
    if (next.get("cat") === cats[0]) next.delete("cat");
    setParams(next, { replace: true });
  };

  // Every route out of this page carries the Calendar's own URL, so
  // BookingDetail's Back returns to this exact view rather than depending on
  // history depth.
  const returnTo = `${location.pathname}${location.search}`;
  const openBooking = (id) => navigate(`/bookings/view/${id}`, { state: { returnTo } });

  // Horizontal scroll is DERIVED, never stored: a saved scrollLeft is wrong at
  // any other width or zoom. With a highlighted day its column is brought into
  // view once, after the grid has rows; with no highlight nothing moves.
  const gridRef = useRef(null);
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || tab !== "availability" || !hlDay) return;
    if (hlDay.getMonth() !== cursor.getMonth() || hlDay.getFullYear() !== cursor.getFullYear()) return;
    const wrap = gridRef.current;
    const col = wrap?.querySelector(`[data-testid="date-header-${hlDay.getDate()}"]`);
    if (!wrap || !col) return;
    const left = col.getBoundingClientRect().left - wrap.getBoundingClientRect().left + wrap.scrollLeft;
    wrap.scrollLeft = Math.max(0, left - wrap.clientWidth / 2);
    restoredRef.current = true;
  }, [tab, hlDay, cursor, cat, monthDays, grouped]);
  const vehicleBookings = useMemo(() => {
    const m = {};
    for (const b of bookings) {
      if (!b.vehicle_id || b.status === "cancelled" || b.status === "completed" || b.status === "rejected") continue;
      const s = dateOnly(b.start_datetime); const e = dateOnly(b.end_datetime) || s;
      if (!s) continue;
      (m[b.vehicle_id] ||= []).push({ s, e, b });
    }
    return m;
  }, [bookings]);

  const todayView = useMemo(() => computeTodayAvailability(vehicles, bookings), [bookings, vehicles]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <Tabs value={tab} onValueChange={(v) => setView({ tab: v })}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList className="max-w-full overflow-x-auto">
            {FILTERS.filter((f) => !(isInvestor && (f.value === "today" || f.value === "availability"))).map((f) => <TabsTrigger key={f.value} value={f.value} data-testid={`calendar-tab-${f.value}`} className="shrink-0">{f.label}</TabsTrigger>)}
          </TabsList>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setView({ month: monthParamOf(addMonths(cursor, -1)) })} data-testid="cal-prev-month"><CaretLeft size={14} /></Button>
            <div className="font-heading text-lg min-w-40 text-center" data-testid="cal-month">{monthLabel}</div>
            <Button variant="outline" size="sm" onClick={() => setView({ month: monthParamOf(addMonths(cursor, 1)) })} data-testid="cal-next-month"><CaretRight size={14} /></Button>
            <Button variant="ghost" size="sm" onClick={() => setView({ month: monthParamOf(new Date()) })} data-testid="cal-today">Today</Button>
          </div>
        </div>

        {FILTERS.filter((f) => f.value !== "availability" && f.value !== "today").map((f) => (
          <TabsContent key={f.value} value={f.value} className="mt-6">
            <div className="border border-border bg-card">
              <div className="grid grid-cols-7 border-b border-border">
                {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-3 py-2 label-mono border-r border-border last:border-r-0">{d}</div>)}
              </div>
              {Array.from({ length: grid.length / 7 }).map((_, r) => {
                const rowSpans = spans.filter((sp) => sp.row === r);
                const maxLane = rowSpans.reduce((m, s) => Math.max(m, s.lane), -1);
                const rowHeight = Math.max(112, 36 + (maxLane + 1) * 22);
                return (
                  <div key={r} className="relative grid grid-cols-7 border-b border-border/40" style={{ minHeight: rowHeight }}>
                    {grid.slice(r * 7, r * 7 + 7).map((day, i) => {
                      const isCur = day.getMonth() === cursor.getMonth();
                      const isToday = sameDay(day, new Date());
                      return (
                        <div key={day.toDateString()} className={`border-r border-border/40 last:border-r-0 p-2 ${!isCur ? "bg-background/40" : ""}`}>
                          <div className={`text-xs ${isToday ? "text-primary font-heading" : isCur ? "text-foreground" : "text-muted-foreground"}`}>{day.getDate()}</div>
                        </div>
                      );
                    })}
                    {rowSpans.map((sp, idx) => (
                      <div key={`${sp.event.id}-${idx}`} onClick={() => openBooking(sp.event.id)} className={`absolute border-l-2 px-2 text-xs truncate cursor-pointer hover:brightness-125 transition-[filter] ${sp.color.bar} ${sp.color.text}`}
                           style={{ left: `${((sp.colStart - 1) / 7) * 100}%`, width: `${(sp.colSpan / 7) * 100}%`, top: 28 + sp.lane * 22, height: 20 }}
                           title={sp.event.booking_type === "internal"
                             ? `Internal hold${sp.event.internal_user ? ` · ${sp.event.internal_user}` : ""}${sp.event.vehicle_label ? ` · ${sp.event.vehicle_label}` : ""}${sp.event.internal_reason ? `\nReason: ${sp.event.internal_reason}` : ""}\nOut: ${fmtDT(sp.event.start_datetime)}\nReturns: ${fmtDT(sp.event.end_datetime)}`
                             : `${sp.event.customer_name || "Guest"}${sp.event.vehicle_label ? ` · ${sp.event.vehicle_label}` : sp.event.tour_name ? ` · ${sp.event.tour_name}` : ""}${sp.event.driver_name ? `\nDriver: ${sp.event.driver_name}` : ""}\nOut: ${fmtDT(sp.event.start_datetime)}\nReturns: ${fmtDT(sp.event.end_datetime)}`}>
                        {sp.event.booking_type === "internal"
                          ? `${sp.event.internal_user || "Internal hold"}${sp.event.vehicle_label ? ` · ${sp.event.vehicle_label}` : ""}`
                          : `${sp.event.customer_name}${sp.event.vehicle_label ? ` · ${sp.event.vehicle_label}` : ""}`}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            {/* The month view's own three, mirroring the bars' /30 wash and
                left border. A colour nobody can name is decoration. */}
            <div className="mt-3 flex gap-4 text-xs text-muted-foreground flex-wrap" data-testid="month-legend">
              <span className="flex items-center gap-1.5" data-testid="month-legend-rental"><span className="w-3 h-3 bg-primary/30 border-l-2 border-primary inline-block" /> Rental</span>
              <span className="flex items-center gap-1.5" data-testid="month-legend-insurance"><span className="w-3 h-3 bg-pink-500/30 border-l-2 border-pink-500 inline-block" /> Insurance replacement</span>
              <span className="flex items-center gap-1.5" data-testid="month-legend-chauffeur"><span className="w-3 h-3 bg-purple-500/30 border-l-2 border-purple-500/40 inline-block" /> Chauffeur / Tour</span>
              <span className="flex items-center gap-1.5" data-testid="month-legend-internal"><span className={`w-3 h-3 border-l-2 inline-block ${INTERNAL_BAR}`} /> Internal</span>
            </div>
          </TabsContent>
        ))}

        {/* Available today */}
        <TabsContent value="today" className="mt-6">
          <TodayAvailabilityPanel today={todayView} onOpen={(id) => navigate(`/bookings/view/${id}`)} />
        </TabsContent>

        {/* Fleet availability matrix */}
        <TabsContent value="availability" className="mt-6">
          <Tabs value={cat} onValueChange={(v) => setView({ cat: v })}>
            <TabsList className="mb-4">
              {CATS.filter((c) => grouped[c].length > 0).map((cat) => (
                <TabsTrigger key={cat} value={cat} data-testid={`avail-tab-${cat.toLowerCase()}`}>
                  {cat} <span className="ml-2 text-xs text-muted-foreground">{grouped[cat].length}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            {CATS.filter((c) => grouped[c].length > 0).map((cat) => (
              <TabsContent key={cat} value={cat}>
                <div ref={gridRef} className="border border-border bg-card overflow-x-auto">
                  <div className="grid" style={{ gridTemplateColumns: `200px repeat(${monthDays.length}, minmax(26px,1fr))` }}>
                    <div className="px-3 py-2 label-mono border-b border-r border-border bg-card sticky left-0">{cat}</div>
                    {monthDays.map((d) => {
                      const dk = d.toDateString();
                      const colActive = hlDate === dk;
                      return (
                        <div key={dk} onClick={() => setView({ day: colActive ? null : dayParamOf(d) })} data-testid={`date-header-${d.getDate()}`}
                          className={`text-center py-2 text-xs border-b border-r-2 border-border cursor-pointer transition-colors hover:bg-secondary/40 ${sameDay(d, new Date()) ? "text-primary font-heading" : "text-muted-foreground"} ${colActive ? "ring-1 ring-inset ring-primary bg-primary/10 font-heading text-primary" : ""} ${hlDate && !colActive ? "opacity-40" : ""}`}>{d.getDate()}</div>
                      );
                    })}
                    {grouped[cat].flatMap((v) => [
                      <div key={`l-${v.id}`} onClick={() => setView({ vehicle: hlVehicle === v.id ? null : v.id })} data-testid={`vehicle-label-${v.id}`}
                        className={`px-3 py-1 text-xs border-b border-r border-border/40 sticky left-0 bg-card cursor-pointer transition-colors hover:bg-secondary/40 ${hlVehicle === v.id ? "ring-1 ring-inset ring-primary bg-primary/10" : ""} ${hlVehicle && hlVehicle !== v.id ? "opacity-40" : ""}`}>
                        <div className="font-heading truncate">{v.make} {v.model}{v.status === "maintenance" && <span className="ml-1 text-orange-500 font-mono text-[9px]">● MAINT</span>}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{v.license_plate}</div>
                      </div>,
                      ...monthDays.map((d) => {
                        const dk = d.toDateString();
                        const rowActive = hlVehicle === v.id;
                        const colActive = hlDate === dk;
                        const anyHl = hlVehicle || hlDate;
                        const rowHl = (rowActive || colActive) ? "ring-2 ring-inset ring-primary z-10 relative" : (anyHl ? "opacity-40" : "");
                        if (v.status === "maintenance") {
                          return (
                            <div key={`${v.id}-${d.getDate()}`} className={`flex items-center justify-center border-b border-r-2 border-border bg-orange-500/70 ${rowHl}`} style={{ minHeight: 24 }} title="In maintenance — unavailable" data-testid={`maint-cell-${v.id}-${d.getDate()}`}>
                              <span className="text-orange-950 font-bold text-[11px] leading-none">✕</span>
                            </div>
                          );
                        }
                        const segs = vehicleBookings[v.id] || [];
                        const hits = segs.filter((s) => d >= s.s && d <= s.e);
                        let cls = "bg-emerald-500/10", title = "Available", target = null;
                        const typeLabel = (b) => (b.booking_type === "internal" ? "Internal hold"
                          : b.booking_type === "chauffeur" ? "Chauffeur"
                          : b.booking_type === "tour" ? "Tour"
                          : b.booking_category === "insurance" ? "Insurance replacement" : "Rental");
                        if (hits.length) {
                          const confirmed = hits.find((h) => h.b.status !== "submitted");
                          const sel = confirmed || hits[0];
                          target = sel.b;
                          const selChauffeurTour = sel.b.booking_type === "chauffeur" || sel.b.booking_type === "tour";
                          // This grid keeps its OWN palette — solid /60, no borders — and never
                          // calls colorForEvent, so the insurance pink is stated here as well.
                          // Two rules in two places is the cost of two palettes; sharing one
                          // would restyle every booking type in this grid.
                          const selInsurance = sel.b.booking_type === "rental" && sel.b.booking_category === "insurance";
                          const selInternal = sel.b.booking_type === "internal";
                          cls = selInternal
                            ? INTERNAL_CELL
                            : confirmed
                              ? (selChauffeurTour ? "bg-blue-500/60" : selInsurance ? "bg-pink-500/60" : "bg-red-500/60")
                              : PENDING_CELL;
                          if (hits.length > 1) {
                            title = `${hits.length} bookings on this day:\n` + hits
                              .map((h) => `• ${h.b.status !== "submitted" ? "Confirmed" : "Pending approval"} · ${typeLabel(h.b)} · ${h.b.booking_type === "internal" ? (h.b.internal_user || "internal") : (h.b.customer_name || "Guest")}${h.b.driver_name ? ` · driver ${h.b.driver_name}` : ""} · out ${fmtDT(h.b.start_datetime)} · returns ${fmtDT(h.b.end_datetime)}${h.b.source === "website" ? " · from the website" : ""}`)
                              .join("\n");
                          } else if (sel.b.booking_type === "internal") {
                            title = `Internal hold · ${typeLabel(sel.b)}\nUsed by: ${sel.b.internal_user || "—"}${sel.b.internal_reason ? `\nReason: ${sel.b.internal_reason}` : ""}\nOut: ${fmtDT(sel.b.start_datetime)}\nReturns: ${fmtDT(sel.b.end_datetime)}`;
                          } else {
                            title = `${confirmed ? "Confirmed" : "Pending approval, not confirmed yet"} · ${typeLabel(sel.b)}\nClient: ${sel.b.customer_name || "Guest"}\nSalesperson: ${sel.b.salesperson || "—"}${sel.b.driver_name ? `\nDriver: ${sel.b.driver_name}` : ""}\nOut: ${fmtDT(sel.b.start_datetime)}\nReturns: ${fmtDT(sel.b.end_datetime)}`;
                          }
                        }
                        const stars = hits.length >= 2 ? "★".repeat(hits.length) : "";
                        // CAL-002: a day is "from the website" if ANY booking in the
                        // cell came from the public website — not only the one shown —
                        // so a website booking sharing the day with another still shows
                        // the "W". The multi-booking hover marks that booking's line.
                        // No cell colour changes.
                        const fromWebsite = hits.some((h) => h.b?.source === "website");
                        if (fromWebsite && hits.length <= 1) title += "\nCame from the website";
                        // SALES-011: a double-booked day opens the switcher —
                        // navigating straight to `target` made every booking but
                        // the selected one unreachable from the cell that listed
                        // them. Sorted by start: segment order is document order,
                        // so tab numbering would otherwise shift between renders.
                        const openCell = () => {
                          if (!target) return;
                          if (hits.length > 1) {
                            setPicker({
                              vehicle: v,
                              date: d,
                              hits: [...hits]
                                .sort((x, y) => String(x.b.start_datetime || "").localeCompare(String(y.b.start_datetime || "")))
                                .map((h) => ({
                                  id: h.b.id,
                                  status: h.b.status,
                                  type: h.b.booking_type,
                                  out: h.b.start_datetime,
                                  ret: h.b.end_datetime,
                                  customer: h.b.customer_name || "",
                                  agent: h.b.salesperson || "",
                                  from: h.b.start_location || "",
                                  to: h.b.end_location || "",
                                  category: h.b.booking_category,
                                  driver: h.b.driver_name || "",
                                })),
                            });
                            return;
                          }
                          openBooking(target.id);
                        };
                        return (
                          <div key={`${v.id}-${d.getDate()}`} onClick={target ? openCell : undefined}
                               className={`flex items-center justify-center border-b border-r-2 border-border ${cls} ${target ? "cursor-pointer hover:brightness-125 transition-[filter]" : ""} ${rowHl}`}
                               style={{ minHeight: 24 }} title={title}
                               data-testid={hits.length >= 2 ? `double-book-cell-${v.id}-${d.getDate()}` : `cal-avail-cell-${v.id}-${d.getDate()}`}>
                            {fromWebsite && <span className="text-[8px] leading-none font-bold text-black tracking-tighter" data-testid={`cal-website-${v.id}-${d.getDate()}`}>W</span>}
                            {stars && <span className="text-[8px] leading-none font-bold text-black tracking-tighter">{stars}</span>}
                          </div>
                        );
                      })
                    ])}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
          <div className="mt-3 flex gap-4 text-xs text-muted-foreground flex-wrap" data-testid="availability-legend">
            <span className="flex items-center gap-1.5" data-testid="legend-available"><span className="w-3 h-3 bg-emerald-500/30 inline-block" /> Available</span>
            <span className="flex items-center gap-1.5" data-testid="legend-submitted"><span className={`w-3 h-3 inline-block ${PENDING_CELL}`} /> Pending approval (not confirmed yet)</span>
            <span className="flex items-center gap-1.5" data-testid="legend-confirmed-rental"><span className="w-3 h-3 bg-red-500/60 inline-block" /> Confirmed rental</span>
            <span className="flex items-center gap-1.5" data-testid="legend-insurance"><span className="w-3 h-3 bg-pink-500/60 inline-block" /> Insurance replacement</span>
            <span className="flex items-center gap-1.5" data-testid="legend-confirmed-chauffeur"><span className="w-3 h-3 bg-blue-500/60 inline-block" /> Confirmed chauffeur / tour</span>
            <span className="flex items-center gap-1.5" data-testid="legend-internal"><span className={`w-3 h-3 inline-block ${INTERNAL_CELL}`} /> Internal hold</span>
            <span className="flex items-center gap-1.5" data-testid="legend-double-booked"><span className="inline-flex items-center justify-center w-4 h-3.5 bg-red-500/60 text-black text-[8px] font-bold">★★</span> Double-booked (one ★ per booking)</span>
            <span className="flex items-center gap-1.5" data-testid="legend-website"><span className="inline-flex items-center justify-center w-4 h-3.5 bg-red-500/60 text-black text-[8px] font-bold">W</span> From the website</span>
          </div>
        </TabsContent>
      </Tabs>
      <DayBookingsDialog picker={picker} onClose={() => setPicker(null)} onOpenBooking={openBooking} />
    </div>
  );
}
